import { useEffect, useState } from "react";
import { currentSubscription, isIOS, isStandalone, pushSupported, subscribe, unsubscribe } from "../push";

function rememberedDriverId(): number | null {
  try {
    const id = JSON.parse(localStorage.getItem("driver") ?? "null")?.id;
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

/** Header bell: turns "new task" push notifications on/off for this phone. */
export default function NotifyBell() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (!pushSupported()) {
      setEnabled(null);
      return;
    }
    currentSubscription().then((sub) => setEnabled(!!sub));
  }, []);

  useEffect(() => {
    if (!hint) return;
    const t = setTimeout(() => setHint(null), 6000);
    return () => clearTimeout(t);
  }, [hint]);

  if (!pushSupported()) return null;
  // Push on iPhone only works from the installed Home Screen app.
  const needsInstall = isIOS() && !isStandalone();

  const toggle = async () => {
    if (busy) return;
    if (needsInstall) {
      setHint("Add Status App to your Home Screen (Share → Add to Home Screen) to get notifications.");
      return;
    }
    setBusy(true);
    try {
      if (enabled) {
        await unsubscribe();
        setEnabled(false);
        setHint("Notifications off for this phone.");
      } else {
        await subscribe(rememberedDriverId());
        setEnabled(true);
        setHint("You'll be notified on this phone when a task is added.");
      }
    } catch (err) {
      setHint(err instanceof Error ? err.message : "Couldn't change notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={toggle}
        disabled={busy}
        aria-label={enabled ? "Turn off notifications" : "Turn on notifications"}
        title={enabled ? "Notifications on" : "Notifications off"}
        className={`flex size-10 items-center justify-center rounded-full transition-transform active:scale-95 disabled:opacity-60 ${
          enabled ? "bg-blush text-rose-ink" : "border border-line bg-white text-ink-soft"
        }`}
      >
        <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
          {enabled === false && <path d="M4 4l16 16" />}
        </svg>
      </button>
      {hint && (
        <div className="pop-in fixed inset-x-4 top-4 z-50 mx-auto max-w-md rounded-[14px] bg-ink px-4 py-3 text-center text-sm font-semibold text-white shadow-pop">
          {hint}
        </div>
      )}
    </>
  );
}
